// pages/overlay/TitleCard.jsx
// Vertical agency-pitch reel title card. Mirrors the Stinger pattern:
//   /overlay/titlecard          — preview: auto-loops in the browser
//   /overlay/titlecard?render   — frame-by-frame canvas capture → WebM download
//
// Background mode (applies to both preview and render):
//   ?bg=transparent (default) — alpha channel preserved (for CapCut alpha import)
//   ?bg=green                 — solid #00FF00 (chroma-key fallback)
//
// Animation arc (~6s):
//   0.0–0.6s  JOP master logo + "presents" fade in at top
//   0.3–1.3s  scalloped badge scales up with gentle overshoot
//   0.4–1.3s  4-point sparkle accents fade in (staggered, replace prior starburst)
//   0.8–1.8s  main title fades in inside the badge
//   1.8–2.8s  subtitle pill slides up from below
//   2.8–3.8s  credential strip wordmarks reveal staggered
//   3.5–6.0s  hold + ambient: badge breath, sparkle opacity shimmer

import { useEffect, useRef, useState } from "react";

const W = 1080;
const H = 1920;
const FPS = 60;
const TOTAL_MS = 6000;
const TOTAL_FRAMES = Math.ceil((TOTAL_MS / 1000) * FPS);

// Brand palette
const NAVY      = "#1d52a0";
const ORANGE    = "#f57600";
const GOLD      = "#fcb31f";
const CREAM     = "#ffdaad";

// Layout — badge is hero, JOP presence at top, supporting elements below
const BADGE_CX = W / 2;
const BADGE_CY = 800;
const BADGE_W  = 880;
const BADGE_H  = 600;

const JOP_LOGO_HEIGHT = 150;
const JOP_LOGO_Y      = 280;  // top y for the logo
const PRESENTS_Y      = 430;

// Per-element timing (ms)
const JOP_START      = 0;
const JOP_DUR        = 600;
const BADGE_START    = 300;
const BADGE_DUR      = 1000;
const TITLE_START    = 800;
const TITLE_DUR      = 1000;
const SUBTITLE_START = 1800;
const SUBTITLE_DUR   = 1000;
const CRED_START     = 2800;
const CRED_STAGGER_MS = 110;
const HOLD_START     = 3500;

// Decorative sparkles — 4-point stars matching JOP logo sparkle style.
// 4 main + 2 tertiary fill sparkles for added magick density.
const SPARKLES = [
  { x: 940, y: 280,  size: 40, color: GOLD,  fadeStart: 400,  phaseOffset: 0     },
  { x: 140, y: 580,  size: 30, color: GOLD,  fadeStart: 600,  phaseOffset: 0.7   },
  { x: 970, y: 1090, size: 34, color: GOLD,  fadeStart: 800,  phaseOffset: 1.4   },
  { x: 175, y: 1660, size: 32, color: GOLD,  fadeStart: 1000, phaseOffset: 2.1   },
  { x: 810, y: 1560, size: 18, color: GOLD,  fadeStart: 1200, phaseOffset: 0.4   },
  { x: 290, y: 1190, size: 16, color: GOLD,  fadeStart: 1300, phaseOffset: 1.7   },
];

// Credentials — two lines, biggest TV first
const CRED_LINE_1 = ["TODAY", "GMA", "HELL'S KITCHEN"];
const CRED_LINE_2 = ["DISNEY", "NICKELODEON"];

// ── EASING ───────────────────────────────────────────────────────────────────
const clamp01 = (t) => Math.max(0, Math.min(1, t));
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const easeOutBack = (t) => {
  const c1 = 1.35; const c3 = c1 + 1; // gentle overshoot, earnest not snappy
  const x = t - 1;
  return 1 + c3 * x * x * x + c1 * x * x;
};

// ── 4-POINT SPARKLE ──────────────────────────────────────────────────────────
// Diamond/star sparkle matching the gold sparkles in the JOP master logo.
// Vertical points reach further than horizontal for that classic 4-point look.
function drawSparkle(ctx, cx, cy, size, color, alpha) {
  if (alpha <= 0.02) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = color;
  ctx.shadowColor = color;
  ctx.shadowBlur = size * 0.6;
  ctx.beginPath();
  ctx.moveTo(cx,                cy - size);
  ctx.lineTo(cx + size * 0.20,  cy - size * 0.20);
  ctx.lineTo(cx + size,         cy);
  ctx.lineTo(cx + size * 0.20,  cy + size * 0.20);
  ctx.lineTo(cx,                cy + size);
  ctx.lineTo(cx - size * 0.20,  cy + size * 0.20);
  ctx.lineTo(cx - size,         cy);
  ctx.lineTo(cx - size * 0.20,  cy - size * 0.20);
  ctx.closePath();
  ctx.fill();
  // Tiny cream hot spot at center
  ctx.shadowBlur = 0;
  ctx.fillStyle = `rgba(255, 240, 210, ${alpha * 0.85})`;
  ctx.beginPath();
  ctx.arc(cx, cy, size * 0.16, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawAllSparkles(ctx, ms) {
  SPARKLES.forEach(({ x, y, size, color, fadeStart, phaseOffset }) => {
    const inP = clamp01((ms - fadeStart) / 500);
    if (inP <= 0) return;
    const baseAlpha = easeOutCubic(inP);
    // Ambient opacity shimmer during hold — 3s period, gentle range
    let shimmer = 1;
    if (ms > HOLD_START) {
      const t = (ms - HOLD_START) / 1000 + phaseOffset;
      shimmer = 0.78 + 0.22 * (0.5 + 0.5 * Math.sin(t * Math.PI * 0.66));
    }
    drawSparkle(ctx, x, y, size, color, baseAlpha * shimmer);
  });
}

// ── JOP HEADER ───────────────────────────────────────────────────────────────
// Master JOP logo + "presents" word, treated like an opening film credit.
// Subtle, supporting, sets up the hero reveal below.
function drawJoPHeader(ctx, jopImg, ms) {
  const inP = clamp01((ms - JOP_START) / JOP_DUR);
  if (inP <= 0 || !jopImg) return;
  const alpha = easeOutCubic(inP);

  ctx.save();
  ctx.globalAlpha = alpha * 0.95;
  const logoH = JOP_LOGO_HEIGHT;
  const logoW = jopImg.width * (logoH / jopImg.height);
  ctx.drawImage(jopImg, (W - logoW) / 2, JOP_LOGO_Y, logoW, logoH);
  ctx.restore();

  // "presents" in Lobster Two italic, navy with white outline for legibility
  // against arbitrary video backgrounds in the reel.
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.font = "italic 400 44px 'Lobster Two', cursive";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";
  ctx.lineWidth = 5;
  ctx.strokeStyle = "rgba(255,255,255,0.95)";
  ctx.strokeText("presents", W / 2, PRESENTS_Y);
  ctx.fillStyle = NAVY;
  ctx.fillText("presents", W / 2, PRESENTS_Y);
  ctx.restore();
}

// ── BADGE HALO ───────────────────────────────────────────────────────────────
// Soft warm gold radial glow behind the badge. Adds magick without competing.
function drawBadgeHalo(ctx, cx, cy, w, h, alpha) {
  if (alpha <= 0) return;
  const grad = ctx.createRadialGradient(cx, cy, w * 0.25, cx, cy, w * 0.95);
  grad.addColorStop(0,    `rgba(252, 179, 31, ${0.22 * alpha})`);
  grad.addColorStop(0.45, `rgba(252, 179, 31, ${0.10 * alpha})`);
  grad.addColorStop(1,    `rgba(252, 179, 31, 0)`);
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.ellipse(cx, cy, w * 0.95, h * 1.05, 0, 0, Math.PI * 2);
  ctx.fill();
}

// ── BADGE (smooth oval, layered rings) ──────────────────────────────────────
// Clean vintage-medallion shape — navy rim, gold trim band, cream inner panel,
// inscribed navy hairline. Gold trim has a subtle warm-brightness pulse
// during hold for that extra 15% magick.
function drawBadge(ctx, cx, cy, w, h, ms) {
  const rx = w / 2;
  const ry = h / 2;

  // Gold trim color — base GOLD, warm-brightens slightly during hold
  let trimGold = GOLD;
  if (ms > HOLD_START) {
    const t = (ms - HOLD_START) / 1000;
    const pulse = 0.5 + 0.5 * Math.sin(t * Math.PI * 0.4);
    const r = Math.round(252 + pulse * 3);
    const g = Math.round(179 + pulse * 36);
    const b = Math.round(31 + pulse * 69);
    trimGold = `rgb(${r}, ${g}, ${b})`;
  }

  // Layer 1: navy outer rim with drop shadow
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.45)";
  ctx.shadowBlur = 40;
  ctx.shadowOffsetY = 16;
  ctx.fillStyle = NAVY;
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Layer 2: gold trim band
  ctx.fillStyle = trimGold;
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx - 8, ry - 8, 0, 0, Math.PI * 2);
  ctx.fill();

  // Layer 3: cream inner panel
  ctx.fillStyle = CREAM;
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx - 28, ry - 28, 0, 0, Math.PI * 2);
  ctx.fill();

  // Layer 4: thin navy hairline inscribed inside cream
  ctx.strokeStyle = "rgba(29,82,160,0.35)";
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx - 50, ry - 50, 0, 0, Math.PI * 2);
  ctx.stroke();
}

// ── MAIN TITLE ───────────────────────────────────────────────────────────────
// "Dr. Dan" inline + "the Pancake Man" below, both in Lilita One — chunky
// rounded balloon-letter face, alphabet-magnet friendly. Sized to sit well
// inside the cream panel with breathing room from the blue borders.
function drawMainTitle(ctx, cx, cy, alpha) {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "center";

  // Hero line: "Dr. Dan"
  const baselineY = cy + 5;
  ctx.save();
  ctx.shadowColor = "rgba(10,28,48,0.35)";
  ctx.shadowBlur = 14;
  ctx.shadowOffsetY = 6;
  ctx.font = "400 142px 'Chango', serif";
  ctx.fillStyle = NAVY;
  ctx.fillText("Dr. Dan", cx, baselineY);
  ctx.restore();

  // "the Pancake Man" — sub-line tightened up below the hero to remove
  // the dead space. Baseline tracks just under the hero descender area.
  ctx.font = "400 56px 'Chango', serif";
  ctx.fillStyle = NAVY;
  ctx.fillText("the Pancake Man", cx, cy + 100);

  ctx.restore();
}

// ── SUBTITLE ─────────────────────────────────────────────────────────────────
// Lobster Two italic, navy fill + white outline. Matches the "presents" voice
// for a warm script credential — no pill, floats over the background with
// outline for legibility against arbitrary video clips.
function drawSubtitle(ctx, cx, cy, progress) {
  if (progress <= 0) return;
  const eased = easeOutCubic(progress);
  const offsetY = (1 - eased) * 40;
  const alpha = eased;

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(0, offsetY);

  ctx.font = "italic 700 48px 'Lobster Two', cursive";
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";

  // Split into three parts so the bullet can be orange like the cred strip
  const partA = "the original live pancake artist";
  const partB = "·";
  const partC = "Since 2009";
  const gap = ctx.measureText("  ").width;
  const wA = ctx.measureText(partA).width;
  const wB = ctx.measureText(partB).width;
  const wC = ctx.measureText(partC).width;
  const totalW = wA + gap + wB + gap + wC;
  const x0 = cx - totalW / 2;
  const xB = x0 + wA + gap;
  const xC = xB + wB + gap;

  // Outline pass — white for legibility over video
  ctx.lineJoin = "round";
  ctx.lineWidth = 7;
  ctx.strokeStyle = "rgba(255,255,255,0.95)";
  ctx.strokeText(partA, x0, cy);
  ctx.strokeText(partB, xB, cy);
  ctx.strokeText(partC, xC, cy);

  // Fill pass
  ctx.fillStyle = NAVY;
  ctx.fillText(partA, x0, cy);
  ctx.fillStyle = ORANGE;
  ctx.fillText(partB, xB, cy);
  ctx.fillStyle = NAVY;
  ctx.fillText(partC, xC, cy);

  ctx.restore();
}

// ── CREDENTIAL STRIP ─────────────────────────────────────────────────────────
function drawCredentials(ctx, cx, topY, ms) {
  const labelP = clamp01((ms - CRED_START) / 350);
  if (labelP <= 0) return;

  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  ctx.globalAlpha = easeOutCubic(labelP);
  ctx.font = "700 32px Fredoka, sans-serif";
  ctx.lineJoin = "round";
  ctx.lineWidth = 5;
  ctx.strokeStyle = "rgba(255,255,255,0.95)";
  ctx.fillStyle = GOLD;
  drawTrackedText(ctx, "AS SEEN ON", cx, topY, 7, true);

  ctx.font = "700 46px Fredoka, sans-serif";
  const line1Y = topY + 78;
  const line2Y = topY + 148;

  drawCredLine(ctx, CRED_LINE_1, cx, line1Y, ms, 0);
  drawCredLine(ctx, CRED_LINE_2, cx, line2Y, ms, CRED_LINE_1.length);

  ctx.restore();
}

function drawCredLine(ctx, items, cx, y, ms, indexOffset) {
  const BULLET = " · ";
  const parts = items.map((s) => ctx.measureText(s).width);
  const bulletW = ctx.measureText(BULLET).width;
  const totalW = parts.reduce((a, b) => a + b, 0) + bulletW * (items.length - 1);

  let x = cx - totalW / 2;
  items.forEach((label, i) => {
    const globalIdx = indexOffset + i;
    const start = CRED_START + 200 + globalIdx * CRED_STAGGER_MS;
    const p = clamp01((ms - start) / 400);
    if (p <= 0) { x += parts[i] + (i < items.length - 1 ? bulletW : 0); return; }
    const eased = easeOutCubic(p);

    ctx.save();
    ctx.globalAlpha = eased;
    ctx.translate(0, (1 - eased) * 18);
    ctx.textAlign = "left";
    ctx.lineJoin = "round";
    ctx.lineWidth = 6;
    ctx.strokeStyle = "rgba(255,255,255,0.95)";
    ctx.strokeText(label, x, y);
    ctx.fillStyle = NAVY;
    ctx.fillText(label, x, y);
    ctx.restore();

    x += parts[i];
    if (i < items.length - 1) {
      const bulletP = clamp01((ms - start - 50) / 400);
      ctx.save();
      ctx.globalAlpha = easeOutCubic(bulletP);
      ctx.textAlign = "left";
      ctx.lineJoin = "round";
      ctx.lineWidth = 6;
      ctx.strokeStyle = "rgba(255,255,255,0.95)";
      ctx.strokeText(BULLET, x, y);
      ctx.fillStyle = ORANGE;
      ctx.fillText(BULLET, x, y);
      ctx.restore();
      x += bulletW;
    }
  });
}

// ── HELPERS ──────────────────────────────────────────────────────────────────
function drawTrackedText(ctx, text, cx, y, tracking, outline) {
  const chars = text.split("");
  const widths = chars.map((c) => ctx.measureText(c).width + tracking);
  const total = widths.reduce((a, b) => a + b, 0) - tracking;
  let x = cx - total / 2;
  const align = ctx.textAlign;
  ctx.textAlign = "left";
  chars.forEach((c, i) => {
    if (outline) ctx.strokeText(c, x, y);
    ctx.fillText(c, x, y);
    x += widths[i];
  });
  ctx.textAlign = align;
}

// ── FRAME DRAWING ────────────────────────────────────────────────────────────
function drawFrame(ctx, frameNum, bgMode, jopImg) {
  const ms = (frameNum / FPS) * 1000;

  ctx.clearRect(0, 0, W, H);
  if (bgMode === "green") {
    ctx.fillStyle = "#00FF00";
    ctx.fillRect(0, 0, W, H);
  }

  // JOP header (top of card)
  drawJoPHeader(ctx, jopImg, ms);

  // Decorative sparkles (drawn beneath badge so badge sits cleanly on top)
  drawAllSparkles(ctx, ms);

  // Badge scale-in with overshoot
  const badgeP = clamp01((ms - BADGE_START) / BADGE_DUR);
  if (badgeP > 0) {
    const badgeScale = easeOutBack(badgeP);
    let breath = 1;
    if (ms > HOLD_START) {
      const t = (ms - HOLD_START) / 1000;
      breath = 1 + Math.sin(t * Math.PI * 0.5) * 0.005;
    }
    const totalScale = badgeScale * breath;

    ctx.save();
    ctx.translate(BADGE_CX, BADGE_CY);
    ctx.scale(totalScale, totalScale);
    ctx.translate(-BADGE_CX, -BADGE_CY);
    // Warm gold halo behind badge (fades in with badge)
    drawBadgeHalo(ctx, BADGE_CX, BADGE_CY, BADGE_W, BADGE_H, easeOutCubic(badgeP));
    drawBadge(ctx, BADGE_CX, BADGE_CY, BADGE_W, BADGE_H, ms);

    const titleP = clamp01((ms - TITLE_START) / TITLE_DUR);
    if (titleP > 0) {
      drawMainTitle(ctx, BADGE_CX, BADGE_CY, easeOutCubic(titleP));
    }
    ctx.restore();
  }

  // Subtitle (not scaled with badge)
  const subP = clamp01((ms - SUBTITLE_START) / SUBTITLE_DUR);
  if (subP > 0) {
    drawSubtitle(ctx, BADGE_CX, BADGE_CY + BADGE_H / 2 + 130, subP);
  }

  // Credential strip
  if (ms >= CRED_START) {
    drawCredentials(ctx, BADGE_CX, BADGE_CY + BADGE_H / 2 + 260, ms);
  }
}

// ── ASSET LOADING ────────────────────────────────────────────────────────────
async function ensureAssetsLoaded() {
  // Fonts
  if (document.fonts) {
    await Promise.all([
      document.fonts.load("400 142px 'Chango'"),
      document.fonts.load("400 56px 'Chango'"),
      document.fonts.load("italic 700 48px 'Lobster Two'"),
      document.fonts.load("700 46px Fredoka"),
      document.fonts.load("700 32px Fredoka"),
      document.fonts.load("500 42px Fredoka"),
      document.fonts.load("italic 400 44px 'Lobster Two'"),
    ]);
  }
  // JOP master logo image
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = "/jop_logo_wide.png";
  });
  return { jopImg: img };
}

// ── PREVIEW MODE ─────────────────────────────────────────────────────────────
function TitleCardPreview({ bgMode }) {
  const canvasRef = useRef(null);
  const [assets, setAssets] = useState(null);

  useEffect(() => { ensureAssetsLoaded().then(setAssets); }, []);

  useEffect(() => {
    if (!assets) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let frame = 0;
    let raf;
    function tick() {
      drawFrame(ctx, frame, bgMode, assets.jopImg);
      frame++;
      if (frame > TOTAL_FRAMES + 60) frame = 0;
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [assets, bgMode]);

  return (
    <div style={{ width: "100vw", height: "100vh", background: "#1a1a1a", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
      <canvas
        ref={canvasRef}
        width={W}
        height={H}
        style={{ maxWidth: "100%", maxHeight: "100%", boxShadow: "0 8px 40px rgba(0,0,0,0.5)" }}
      />
      <div style={{ position: "absolute", bottom: "1rem", left: "50%", transform: "translateX(-50%)", color: "rgba(255,255,255,0.55)", fontSize: "0.85rem", fontFamily: "system-ui", textAlign: "center", lineHeight: 1.5 }}>
        Preview · bg={bgMode} · auto-loops<br />
        <span style={{ opacity: 0.7 }}>?render to download · ?bg=green for chroma-key</span>
      </div>
    </div>
  );
}

// ── RENDER MODE — downloads WebM ─────────────────────────────────────────────
function TitleCardRender({ bgMode }) {
  const [status, setStatus] = useState("Loading assets...");
  const canvasRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const assets = await ensureAssetsLoaded();
      if (cancelled) return;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      setStatus("Rendering frames...");
      const stream = canvas.captureStream(0);
      const recorder = new MediaRecorder(stream, {
        mimeType: "video/webm;codecs=vp9",
        videoBitsPerSecond: 12_000_000,
      });
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.start();

      for (let f = 0; f <= TOTAL_FRAMES + 5; f++) {
        drawFrame(ctx, f, bgMode, assets.jopImg);
        const track = stream.getVideoTracks()[0];
        if (track.requestFrame) track.requestFrame();
        await new Promise((r) => setTimeout(r, 1000 / FPS));
        if (f % 30 === 0) setStatus(`Rendering frame ${f}/${TOTAL_FRAMES}...`);
      }

      ctx.clearRect(0, 0, W, H);
      const track = stream.getVideoTracks()[0];
      if (track.requestFrame) track.requestFrame();
      await new Promise((r) => setTimeout(r, 100));

      recorder.stop();
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: "video/webm" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `jop-reel-titlecard-${bgMode}.webm`;
        a.click();
        setStatus(`Downloaded jop-reel-titlecard-${bgMode}.webm`);
      };
    })();
    return () => { cancelled = true; };
  }, [bgMode]);

  return (
    <div style={{ width: "100vw", height: "100vh", background: "#0d1833", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "1.5rem", fontFamily: "system-ui" }}>
      <canvas ref={canvasRef} width={W} height={H} style={{ maxHeight: "75vh", maxWidth: "90vw", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8 }} />
      <div style={{ color: GOLD, fontSize: "1.1rem", fontWeight: 700 }}>{status}</div>
      <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", maxWidth: 600, textAlign: "center", lineHeight: 1.6 }}>
        Rendering vertical title card to WebM (VP9). bg=<b>{bgMode}</b>.
        File downloads automatically when done.
      </div>
    </div>
  );
}

// ── ROUTER ───────────────────────────────────────────────────────────────────
export default function TitleCard() {
  const params = new URLSearchParams(window.location.search);
  const isRender = params.has("render");
  const bgMode = params.get("bg") === "green" ? "green" : "transparent";
  return isRender ? <TitleCardRender bgMode={bgMode} /> : <TitleCardPreview bgMode={bgMode} />;
}
